import { UserRole } from '@/entities/user-role';

import { type Role } from '../../entities/role';
import { type Site } from '../../entities/site';
import { GetRolesQuery } from '../get-roles';

import {
  getNextRoleId,
  sharedState,
  updateRoleUserCounts,
} from './shared-state';
import { MOCK_SITES } from './sites-data';

// Helper function to get sites by IDs
function getSitesByIds(siteIds: string[]): Site[] {
  return MOCK_SITES.filter((site) => siteIds.includes(site.id));
}

// Role CRUD functions
export function getAllRoles(): Role[] {
  updateRoleUserCounts();
  return [...sharedState.roles];
}

export function getRolesPaginated(
  input: GetRolesQuery.Input,
): GetRolesQuery.Output {
  updateRoleUserCounts();
  const allRoles = sharedState.roles;
  const totalCount = allRoles.length;

  const { page, pageSize } = input;
  const offset = (page - 1) * pageSize;
  const roles = allRoles.slice(offset, offset + pageSize);

  // Calculate pagination info
  const currentPage = page;
  const previousPage = currentPage > 1 ? currentPage - 1 : undefined;
  const hasMoreItems = offset + pageSize < totalCount;
  const nextPage = hasMoreItems ? currentPage + 1 : undefined;

  return {
    totalCount,
    previousPage,
    currentPage,
    nextPage,
    roles,
  };
}

export function getRoleById(id: string): Role | undefined {
  updateRoleUserCounts();
  return sharedState.roles.find((role) => role.id === id);
}

export function createRole(data: {
  name: string;
  permission: UserRole;
  siteIds: string[] | null;
}): { role: { id: string } } | null {
  const sites = data.siteIds ? getSitesByIds(data.siteIds) : [];

  const newRole: Role = {
    id: getNextRoleId(),
    name: data.name,
    permission: data.permission,
    sites,
    userCount: 0,
  };

  sharedState.roles.push(newRole);

  return { role: { id: newRole.id } };
}

export function updateRole(
  id: string,
  data: {
    name: string;
    permission: UserRole;
    siteIds: string[] | null;
  },
): { role: { id: string } } | null {
  const roleIndex = sharedState.roles.findIndex((role) => role.id === id);
  if (roleIndex === -1) {
    return null;
  }

  const sites =
    Array.isArray(data.siteIds) && data.siteIds.length > 0
      ? getSitesByIds(data.siteIds)
      : null;

  sharedState.roles[roleIndex] = {
    ...sharedState.roles[roleIndex],
    name: data.name,
    permission: data.permission,
    sites,
  };

  // Update role name in all users that have this role
  sharedState.users.forEach((user) => {
    if (user.role.id === id) {
      user.role.name = data.name;
    }
  });

  return { role: { id } };
}

export function deleteRole(id: string): boolean {
  const roleIndex = sharedState.roles.findIndex((role) => role.id === id);
  if (roleIndex === -1) {
    return false;
  }

  // Check if any users are using this role
  const usersWithRole = sharedState.users.filter((user) => user.role.id === id);
  if (usersWithRole.length > 0) {
    // Cannot delete role if users are assigned to it
    return false;
  }

  sharedState.roles.splice(roleIndex, 1);

  return true;
}

// Function to be called when users are modified to update counts
export function recalculateUserCounts(): void {
  updateRoleUserCounts();
}
